import { useState } from 'react';
import { Mail } from 'lucide-react';
import { useTheme } from '../context/ThemeContext.jsx';
import Header from '../components/common/Header.jsx';
import Footer from '../components/common/Footer.jsx';

function ContactPageContent() {
  const { colors, spacing, fonts, borderRadius, shadows } = useTheme();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    description: '',
    website: '' // Honeypot field - should remain empty
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    // Honeypot check - if filled, it's a bot
    if (formData.website) {
      // Silently reject bot submission
      setTimeout(() => {
        setStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully. We\'ll get back to you within 24 hours.'
        });
        setFormData({ name: '', email: '', subject: '', description: '', website: '' });
        setLoading(false);
      }, 1000);
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      // Exclude honeypot field from submission
      const { website, ...submissionData } = formData;

      const response = await fetch('/api/webhooks/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...submissionData,
          formType: 'contact'
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully. We\'ll get back to you within 24 hours.'
        });
        setFormData({ name: '', email: '', subject: '', description: '' });
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Timeout - but data was sent successfully
        setStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully. We\'ll get back to you within 24 hours.'
        });
        setFormData({ name: '', email: '', subject: '', description: '' });
      } else {
        setStatus({
          type: 'error',
          message: 'Unable to send message. Please try again later.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const pageStyles = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.background,
    fontFamily: fonts.families.primary
  };

  const mainStyles = {
    flex: 1,
    padding: `${spacing.scale['2xl']} ${spacing.scale.lg}`,
    maxWidth: '800px',
    margin: '0 auto',
    width: '100%'
  };

  const containerStyles = {
    backgroundColor: colors.surface,
    padding: spacing.scale['2xl'],
    borderRadius: borderRadius.lg,
    boxShadow: shadows.xl,
    border: `1px solid ${colors.border}`
  };

  const headingStyles = {
    fontSize: fonts.sizes['2xl'].desktop,
    fontWeight: fonts.weights.bold,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.scale.sm,
    fontFamily: fonts.families.secondary
  };

  const subtitleStyles = {
    fontSize: fonts.sizes.base.desktop,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.scale.xl
  };

  const infoBoxStyles = {
    backgroundColor: `${colors.primary}15`,
    border: `1px solid ${colors.primary}`,
    borderRadius: borderRadius.md,
    padding: spacing.scale.lg,
    marginBottom: spacing.scale.xl
  };

  const infoTitleStyles = {
    fontSize: fonts.sizes.lg.desktop,
    fontWeight: fonts.weights.semibold,
    color: colors.primary,
    marginTop: 0,
    marginBottom: spacing.scale.sm
  };

  const contactMethodsStyles = {
    display: 'grid',
    gap: spacing.scale.md,
    marginBottom: spacing.scale.lg
  };

  const contactItemStyles = {
    display: 'flex',
    alignItems: 'center',
    padding: spacing.scale.md,
    backgroundColor: colors.surfaceHover,
    borderRadius: borderRadius.md,
    textDecoration: 'none',
    color: colors.text,
    transition: 'all 0.2s ease',
    border: `1px solid ${colors.border}`
  };

  const labelStyles = {
    display: 'block',
    marginBottom: spacing.scale.xs,
    fontWeight: fonts.weights.semibold,
    color: colors.text,
    fontSize: fonts.sizes.sm.desktop
  };

  const inputStyles = {
    width: '100%',
    padding: spacing.scale.md,
    border: `2px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    fontSize: fonts.sizes.base.desktop,
    marginBottom: spacing.scale.lg,
    boxSizing: 'border-box',
    backgroundColor: colors.background,
    color: colors.text,
    fontFamily: fonts.families.primary,
    transition: 'border-color 0.2s ease'
  };

  const textareaStyles = {
    ...inputStyles,
    resize: 'vertical',
    minHeight: '120px'
  };

  const isDarkMode = colors.background === '#151b23'; // Check if dark mode

  const buttonStyles = {
    background: loading ? colors.surfaceHover : (isDarkMode ? colors.secondary : colors.primary),
    color: colors.textInverse,
    padding: `${spacing.scale.md} ${spacing.scale.xl}`,
    border: 'none',
    borderRadius: borderRadius.md,
    fontSize: fonts.sizes.base.desktop,
    fontWeight: fonts.weights.semibold,
    cursor: loading ? 'not-allowed' : 'pointer',
    width: '100%',
    transition: 'all 0.2s ease',
    boxShadow: loading ? 'none' : shadows.md
  };

  const statusStyles = {
    padding: spacing.scale.md,
    marginBottom: spacing.scale.lg,
    borderRadius: borderRadius.md,
    backgroundColor: status.type === 'success' ? `${colors.success}20` : `${colors.error}20`,
    color: status.type === 'success' ? colors.success : colors.error,
    border: `1px solid ${status.type === 'success' ? colors.success : colors.error}`
  };

  const privacyNoticeStyles = {
    marginTop: spacing.scale.xl,
    padding: spacing.scale.lg,
    backgroundColor: colors.surfaceHover,
    borderRadius: borderRadius.md,
    fontSize: fonts.sizes.sm.desktop,
    color: colors.textSecondary
  };

  return (
    <div style={pageStyles}>
      <Header />

      <main style={mainStyles}>
        <div style={containerStyles}>
          <h1 style={headingStyles}>Contact Us</h1>
          <p style={subtitleStyles}>Get in touch with Victoria Londe</p>

          <div style={contactMethodsStyles}>
            <a
              href="mailto:hello@victorialonde.com"
              style={contactItemStyles}
              aria-label="Email us at hello@victorialonde.com"
            >
              <Mail size={24} style={{ marginRight: spacing.scale.md, flexShrink: 0 }} aria-hidden="true" />
              <div>
                <strong>General Inquiries</strong><br />
                <span style={{ color: colors.textSecondary }}>hello@victorialonde.com</span>
              </div>
            </a>
          </div>

          <div style={privacyNoticeStyles}>
            <strong>Privacy Notice:</strong> Your information will be used solely to respond to your inquiry.
            We respect your privacy and will never share your details with third parties.
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default function ContactPage() {
  return <ContactPageContent />;
}
